import { CommitmentStatusKeys, CommitmentTypes } from '../model/enums'
import type { AcknowledgmentCommitmentItem } from '../model/types'

import {
  buildAcknowledgmentStatusView,
  isCurrentUserCommitment,
} from './buildAcknowledgmentStatusView'

/* The order a plan is signed in: the PBM, then the manager, then the partner. */
const COMMITMENT_TYPE_RANK: Record<string, number> = {
  [CommitmentTypes.PBM]: 0,
  [CommitmentTypes.Manager]: 1,
  [CommitmentTypes.Partner]: 2,
}

const getCommitmentTypeRank = (item: AcknowledgmentCommitmentItem) =>
  COMMITMENT_TYPE_RANK[item.CommitmentType] ?? Object.keys(COMMITMENT_TYPE_RANK).length

/** Optional as the window reads it: a named signer marked optional. */
export const isOptionalSigner = (item: AcknowledgmentCommitmentItem) =>
  item.IsOptional > 0 && Boolean(item.SignerEmail?.trim())

/* A signature the plan changes dropped is open again, whatever its acknowledged flag says: its
   signer signs anew. */
const isDroppedSignature = (item: AcknowledgmentCommitmentItem) =>
  (item.Dropped ?? 0) > 0 || item.CommitmentStatusKey === CommitmentStatusKeys.Dropped

/* The server marks the rows a plan waits for (`SignoffPending`); a row it does not mark is not
   asked for, whatever else it says. */
const isPendingSignature = (item: AcknowledgmentCommitmentItem) => Number(item.SignoffPending) > 0

const isOpenSignature = (item: AcknowledgmentCommitmentItem) =>
  isPendingSignature(item) &&
  (item.IsAcknowledged !== 1 || isDroppedSignature(item)) &&
  Boolean(item.SignerEmail?.trim() || item.SignerName?.trim())

/**
 * Every signature a plan still lacks and the server marks `SignoffPending`, pending or dropped by
 * its changes: the required ones in signing order (PBM,
 * manager, partner), then the reader's own optional row. Another reader's
 * optional row is not theirs to chase.
 */
export const getPlanSigners = (items: AcknowledgmentCommitmentItem[], currentUserEmail: string) => {
  const ordered = buildAcknowledgmentStatusView({ items }).items.filter(isOpenSignature)
  const required = ordered
    .filter((item) => !isOptionalSigner(item))
    .sort((left, right) => getCommitmentTypeRank(left) - getCommitmentTypeRank(right))
  const ownOptional = ordered.filter(
    (item) => isOptionalSigner(item) && isCurrentUserCommitment(item, currentUserEmail)
  )

  return [...required, ...ownOptional]
}
